import { cn } from "@/lib/cn";

const CERTIFICATIONS = [
  {
    name: "Cyber Essentials",
    status: "Certified",
    statusColor: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
    description: "UK government-backed scheme covering firewalls, secure configuration, access control, malware protection, and patch management.",
    logo: { src: "/cert-cyber-essentials.png", className: "absolute right-1 -bottom-2 h-[120%] w-auto" },
  },
  {
    name: "GDPR",
    status: "Aligned",
    statusColor: "text-blue-500 bg-blue-500/10 border-blue-500/20",
    description: "Data processing, storage, and transfer practices aligned to UK and EU General Data Protection Regulation requirements.",
    logo: { src: "/cert-gdpr.png", className: "absolute -right-4 top-1/2 -translate-y-1/2 h-[120%] w-auto" },
  },
  {
    name: "ISO 27001",
    status: "Aligned",
    statusColor: "text-blue-500 bg-blue-500/10 border-blue-500/20",
    description: "Information security management system practices aligned to the ISO 27001 standard.",
    logo: { src: "/cert-iso27001.png", className: "absolute -right-4 top-1/2 -translate-y-1/2 h-[120%] w-auto" },
  },
  {
    name: "SOC 2 Type II",
    status: "Aligned",
    statusColor: "text-blue-500 bg-blue-500/10 border-blue-500/20",
    description: "Security, availability, and confidentiality controls aligned to AICPA SOC 2 Trust Services Criteria.",
    logo: { src: "/cert-soc2.png", className: "absolute -right-4 top-1/2 -translate-y-1/2 h-[120%] w-auto" },
  },
];

const PRACTICES = [
  {
    title: "Data encryption",
    description: "AES-256-GCM at rest. TLS 1.3 in transit. Customer-managed keys available for enterprise deployments.",
  },
  {
    title: "Tenant isolation",
    description: "Logical and network-level isolation between tenants. No shared compute, no shared storage, no cross-tenant data access.",
  },
  {
    title: "Access control",
    description: "Role-based access with least-privilege defaults. SSO integration. MFA enforced for all administrative access.",
  },
  {
    title: "Incident response",
    description: "Documented incident response plan with defined escalation paths. 24-hour notification commitment for security events.",
  },
  {
    title: "Infrastructure",
    description: "Hosted on ISO 27001-certified cloud infrastructure. Redundant across availability zones with automated failover.",
  },
  {
    title: "Vulnerability disclosure",
    description: "Responsible disclosure policy. Security researchers can report vulnerabilities through our coordinated disclosure process.",
  },
];

export function SecurityGrid() {
  return (
    <div>
      {/* Certifications 2×2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
        {CERTIFICATIONS.map((cert) => (
          <div
            key={cert.name}
            className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 md:p-8 relative overflow-hidden"
          >
            <img
              src={cert.logo.src}
              alt=""
              className={`${cert.logo.className} object-contain pointer-events-none`}
              style={{ opacity: 0.07 }}
              loading="lazy"
            />
            <div className="flex items-center justify-between mb-2 relative">
              <h3 className="text-sm font-[family-name:var(--font-mono)] font-medium">
                {cert.name}
              </h3>
              <span
                className={cn(
                  "text-[10px] font-[family-name:var(--font-mono)] px-2 py-0.5 rounded border",
                  cert.statusColor,
                )}
              >
                {cert.status}
              </span>
            </div>
            <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed relative">
              {cert.description}
            </p>
          </div>
        ))}
      </div>

      {/* Security practices 3×2 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
        {PRACTICES.map((practice) => (
          <div
            key={practice.title}
            className="border-t-2 border-[var(--color-border)] pt-4"
          >
            <h3 className="text-sm font-[family-name:var(--font-mono)] font-medium mb-2">
              {practice.title}
            </h3>
            <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed">
              {practice.description}
            </p>
          </div>
        ))}
      </div>

      {/* Closing paragraph */}
      <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed max-w-2xl">
        The COSX founding team has built and operated regulated technology platforms across financial services, legal, and education. Security is not a feature. It is a prerequisite for every product we ship.
      </p>
    </div>
  );
}
